import { create } from 'zustand';
import type { GameClient } from '../app/gameClient';
import type { GameConfig, Simulation } from '../engine/simulation/simulation';
import type { CommandTransport, NetPlayerRow, ReconnectView } from '../networking/transport';
import type { CommandEnvelope } from '../engine/simulation/commands';
import { settingsFromPreset, detectPreset, type GraphicsSettings } from '../engine/rendering/settings';

export type Screen = 'menu' | 'skirmish' | 'multiplayer' | 'load' | 'settings' | 'credits' | 'game' | 'replays';

export interface QueueView {
  label: string;
  key: string;
  isTech: boolean;
  progress: number;
}

export interface SelectionEntry {
  id: number;
  kind: string;
  name: string;
  hp: number;
  maxHp: number;
  owner: number;
  count: number;
  ids: number[];
}

export interface CommandButton {
  id: string;
  label: string;
  hotkey?: string;
  icon: string;
  cost?: number[];
  disabled?: string | null;
  tooltip: string;
  /** 'age' is the age advancement, shown in the selection panel rather than the command card. */
  group: 'train' | 'build' | 'research' | 'action' | 'formation' | 'stance' | 'age';
}

export interface HudState {
  res: number[];
  /** Units currently assigned to gathering each resource (villagers and fishing boats). */
  workers: number[];
  pop: number;
  popCap: number;
  age: number;
  ageName: string;
  civName: string;
  time: number;
  paused: boolean;
  speed: number;
  baseSpeed: number;
  idleVillagers: number;
  selection: SelectionEntry[];
  primary: {
    id: number; kind: string; name: string; description: string; hp: number; maxHp: number; owner: number; ownerName: string;
    attack: string; armor: string; range: string; los: number; speed: string; carry: string; garrison: string; resourceLeft: string; built: number;
    queue: QueueView[]; isOwn: boolean; category: 'unit' | 'building' | 'resource';
    repair: { cost: number[]; repairers: number } | null;
  } | null;
  buttons: CommandButton[];
  mode: string;
  wall: { name: string; started: boolean; pieces: number; cost: number[] } | null;
  buildMenu: boolean;
  gameOver: {
    won: boolean;
    /** The victory condition met ('Economic', 'Conquest'...), empty if not known (a loaded finished game). */
    reason: string;
    /** What the winner did, e.g. "gathered 30,000 resources". */
    detail: string;
    /** Names of the winning players ('You' for the local player); empty for a draw. */
    winners: string[];
  } | null;
  players: { id: number; name: string; color: string; civ: string; age: number; defeated: boolean; score: number; team: number; gathered: number; winner: boolean }[];
  /** The player nearest an economic victory, when that condition is on. */
  economic: { name: string; color: string; gathered: number; target: number } | null;
  network: string | null;
  /** Per-player connection details (networked games only). */
  netPlayers: NetPlayerRow[] | null;
  /** Lost connections being retried, with the deciding player's choice when a cycle runs out. */
  reconnect: ReconnectView | null;
  desync: string | null;
  replay: { tick: number; total: number; speed: number } | null;
  wonderTimers: { team: number; seconds: number; color: string; name: string }[];
}

export interface Notification {
  id: number;
  text: string;
  time: number;
  x?: number;
  z?: number;
  kind: 'info' | 'attack' | 'good' | 'warn';
}

export interface Preferences {
  graphics: GraphicsSettings;
  volume: { master: number; music: number; effects: number; voices: number; ambient: number };
  edgeScroll: boolean;
  scrollSpeed: number;
  showFps: boolean;
  playerName: string;
  uiScale: number;
}

const PREF_KEY = 'ea.prefs.v1';

function loadPrefs(): Preferences {
  const base: Preferences = {
    graphics: settingsFromPreset(detectPreset()),
    volume: { master: 0.8, music: 0.5, effects: 0.8, voices: 0.7, ambient: 0.5 },
    edgeScroll: true,
    scrollSpeed: 1,
    showFps: false,
    playerName: 'Player',
    uiScale: 1,
  };
  try {
    const raw = localStorage.getItem(PREF_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Preferences>;
      return { ...base, ...p, graphics: { ...base.graphics, ...(p.graphics ?? {}) }, volume: { ...base.volume, ...(p.volume ?? {}) } };
    }
  } catch {
    /* storage unavailable */
  }
  return base;
}

export interface AppState {
  screen: Screen;
  /** Which main-menu button led to the skirmish setup (for usage statistics). */
  entry: 'single' | 'skirmish';
  /** The build the server says is live (0 if not known); newer than ours means an update is waiting. */
  liveBuild: number;
  client: GameClient | null;
  hud: HudState | null;
  notifications: Notification[];
  prefs: Preferences;
  pendingConfig: GameConfig | null;
  /** Alternative launch sources: a loaded save, a replay, or a network session. */
  launch: { sim?: Simulation; replay?: CommandEnvelope[]; replayEnd?: number; transport?: CommandTransport; saveName?: string; spectate?: boolean } | null;
  menuOpen: boolean;
  chatOpen: boolean;
  setChatOpen: (o: boolean) => void;
  setScreen: (s: Screen) => void;
  setClient: (c: GameClient | null) => void;
  setHud: (h: HudState) => void;
  notify: (n: Omit<Notification, 'id' | 'time'>) => void;
  setPrefs: (p: Partial<Preferences>) => void;
  setMenuOpen: (o: boolean) => void;
}

let nid = 1;

export const useApp = create<AppState>((set, get) => ({
  screen: 'menu',
  entry: 'skirmish',
  liveBuild: 0,
  client: null,
  hud: null,
  notifications: [],
  prefs: loadPrefs(),
  pendingConfig: null,
  launch: null,
  menuOpen: false,
  chatOpen: false,
  setChatOpen: (chatOpen) => set({ chatOpen }),
  setScreen: (screen) => set({ screen }),
  setClient: (client) => set({ client }),
  setHud: (hud) => set({ hud }),
  notify: (n) => {
    const now = performance.now();
    const list = get().notifications.filter((x) => now - x.time < 8000).slice(-5);
    set({ notifications: [...list, { ...n, id: nid++, time: now }] });
  },
  setPrefs: (p) => {
    const prefs = { ...get().prefs, ...p };
    try {
      localStorage.setItem(PREF_KEY, JSON.stringify(prefs));
    } catch {
      /* ignore */
    }
    set({ prefs });
  },
  setMenuOpen: (menuOpen) => set({ menuOpen }),
}));
